import React, { useEffect } from "react";
import { X, Heart, Target, Sparkles, Quote } from "lucide-react";

// Per-stage editorial content for the explorer modal.
// Each entry is intentionally voiced in the aesthetic of its stage.
const STAGE_DETAILS = {
  1: {
    moods: ["Overwhelmed", "Scattered", "Anxious-buzzy", "Tired but wired", "Restless"],
    goals: [
      "Settle the noise inside before changing anything outside",
      "Build a soft daily baseline of calm",
      "Reclaim quiet as a real practice, not laziness",
    ],
    practices: ["Daily mood check-in", "Box breathing (2 min)", "One honest journal line"],
    quote: "Calm is the starting line, not the destination.",
  },
  2: {
    moods: ["Tender", "Self-critical", "Lonely-soft", "Yearning", "Easily moved"],
    goals: [
      "Practice everyday kindness toward yourself",
      "Soften the inner critic without silencing it",
      "Let yourself want gentleness — and ask for it",
    ],
    practices: ["Journaling with care", "5-4-3-2-1 grounding", "Gentle reframes"],
    quote: "Tender is a strength dressed in lace.",
  },
  3: {
    moods: ["Contemplative", "Foggy", "Stuck in a loop", "Curious", "Quietly heavy"],
    goals: [
      "Study your patterns instead of fighting them",
      "Slow down enough to actually see the shape of things",
      "Build self-awareness that holds under pressure",
    ],
    practices: ["Long-form journaling", "CBT thought reframes", "Coach deep-dives"],
    quote: "Slow study of yourself is the longest game worth playing.",
  },
  4: {
    moods: ["Frustrated", "Ambitious", "Defiant", "Fired up", "Done with shrinking"],
    goals: [
      "Channel raw energy into clean agency",
      "Stand up for what you actually need",
      "Claim space without apology — but with aim",
    ],
    practices: ["Bold reframes", "Sharp journaling", "Coach challenge sessions"],
    quote: "Loud where it counts. Soft where it matters.",
  },
  5: {
    moods: ["Ready", "Clear", "Complete-for-now", "Renewed", "Spacious"],
    goals: [
      "Integrate everything the earlier stages taught you",
      "Reset cleanly for whatever season comes next",
      "Mentor the version of you that started Stage 1",
    ],
    practices: ["Mastery rotation", "Weekly reflection rituals", "Holding space for others"],
    quote: "Reboot in soft chrome. Nothing is lost, only refined.",
  },
};

// Tiny helper: 95%-opaque hex for the overlay color
const withAlpha = (hex, alpha = "f2") => `${hex}${alpha}`;

const Section = ({ icon: Icon, label, items, accent, text }) => (
  <div data-testid={`modal-section-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="flex items-center gap-3 mb-4">
      <div
        className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: accent, color: text }}
      >
        <Icon size={18} strokeWidth={2.5} />
      </div>
      <div className="text-[11px] sm:text-xs font-bold uppercase tracking-[0.22em] opacity-70">
        {label}
      </div>
    </div>
    <ul className="space-y-2.5">
      {items.map((item, i) => (
        <li
          key={i}
          className="font-medium leading-snug flex items-start gap-2.5"
          style={{ fontSize: "clamp(0.95rem, 1.4vw, 1.125rem)" }}
        >
          <span
            className="inline-block w-1.5 h-1.5 rounded-full flex-shrink-0 mt-2"
            style={{ backgroundColor: accent }}
          />
          <span className="flex-1">{item}</span>
        </li>
      ))}
    </ul>
  </div>
);

/**
 * Full-screen overlay that opens when a user clicks a stage card on the
 * landing page. Background is the stage's own palette at 95% opacity with a
 * backdrop blur, so it feels like stepping INTO that stage's vibe.
 *
 * - All typography uses the stage's `text` color for body and `accent` for
 *   highlights & badges
 * - ESC and overlay-click both dismiss; visible round close button top-right
 * - Fluid sizing via clamp() so it works on phone, tablet and desktop
 */
export const StageDetailModal = ({ stage, onClose }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  if (!stage) return null;
  const details = STAGE_DETAILS[stage.level];
  // Per-spec: on Stage 3 (Mocha Calm) the close pill text + section icons take
  // the stage background color so they ghost cleanly into the mocha accent.
  const iconFg = stage.level === 3 ? stage.background : stage.text;

  return (
    <div
      data-testid="stage-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="stage-modal-title"
      className="fixed inset-0 z-50 flex items-start sm:items-center justify-center px-4 sm:px-6 py-10 overflow-y-auto"
      style={{
        backgroundColor: withAlpha(stage.background, "f2"),
        backdropFilter: "blur(24px)",
        WebkitBackdropFilter: "blur(24px)",
        color: stage.text,
        animation: "fadeUp 0.35s ease-out both",
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-4xl"
      >
        {/* Close — friendly, clearly labelled, scales with viewport */}
        <button
          onClick={onClose}
          data-testid="stage-modal-close"
          aria-label="Close stage details"
          className="absolute right-0 -top-2 sm:top-0 flex items-center gap-2 rounded-full font-bold uppercase tracking-widest transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2"
          style={{
            backgroundColor: stage.accent,
            color: iconFg,
            paddingInline: "clamp(14px, 1.6vw, 22px)",
            height: "clamp(40px, 4.4vw, 52px)",
            fontSize: "clamp(10px, 1vw, 12px)",
            letterSpacing: "0.18em",
            boxShadow: "0 6px 18px rgba(0,0,0,0.10)",
          }}
        >
          <X size={16} strokeWidth={3} />
          <span className="hidden sm:inline">Close</span>
        </button>

        {/* Header */}
        <div className="pr-24 sm:pr-32">
          <div
            className="font-bold uppercase opacity-60"
            style={{
              fontSize: "clamp(11px, 1.1vw, 13px)",
              letterSpacing: "0.28em",
            }}
          >
            Stage {stage.level} · {stage.aesthetic}
          </div>
          <h2
            id="stage-modal-title"
            className="font-display font-black tracking-tighter mt-3 leading-[0.92]"
            style={{ fontSize: "clamp(2.6rem, 8vw, 6rem)" }}
          >
            {stage.name}
          </h2>
          <p
            className="italic mt-4 opacity-85 max-w-2xl"
            style={{ fontSize: "clamp(1.05rem, 2.2vw, 1.6rem)", lineHeight: 1.25 }}
          >
            {stage.tagline}
          </p>
          <div
            className="mt-6 h-2 rounded-full"
            style={{ backgroundColor: stage.accent, width: "clamp(80px, 12vw, 140px)" }}
          />
        </div>

        {/* Three content sections */}
        <div className="grid sm:grid-cols-3 gap-8 sm:gap-10 mt-10 sm:mt-14 pr-2 sm:pr-0">
          <Section
            icon={Heart}
            label="Moods this stage holds"
            items={details.moods}
            accent={stage.accent}
            text={iconFg}
          />
          <Section
            icon={Target}
            label="What you train for"
            items={details.goals}
            accent={stage.accent}
            text={iconFg}
          />
          <Section
            icon={Sparkles}
            label="Daily practices"
            items={details.practices}
            accent={stage.accent}
            text={iconFg}
          />
        </div>

        {/* Editorial quote — vibe statement */}
        <div
          className="mt-12 sm:mt-14 p-6 sm:p-8 rounded-3xl flex items-start gap-4"
          style={{
            backgroundColor: `${stage.accent}55`,
            border: `1.5px solid ${stage.accent}`,
          }}
        >
          <Quote size={28} className="opacity-50 flex-shrink-0 mt-1" />
          <div
            className="font-display italic leading-snug"
            style={{ fontSize: "clamp(1.15rem, 2.4vw, 1.75rem)" }}
          >
            {details.quote}
          </div>
        </div>

        {/* Footer hint */}
        <div className="mt-8 text-center text-xs opacity-50 tracking-wider">
          Press <kbd className="px-1.5 py-0.5 rounded border" style={{ borderColor: `${stage.text}40` }}>esc</kbd> or click anywhere outside to close
        </div>
      </div>
    </div>
  );
};

export default StageDetailModal;
